import { Avatar } from '@sketchnote/shared-components';
import {
  Comments,
  resetEditorValue,
  useComments,
  useSlatePluginsStore,
} from '@sketchnote/slate-plugins';
import {
  comboboxActions,
  MentionCombobox,
  Plate,
  PlateEditor,
  PlatePlugin,
  TDescendant,
  usePlateEditorRef,
} from '@udecode/plate';
import cn from 'classnames';
import * as React from 'react';
import { EditableProps } from 'slate-react/dist/components/editable';
import { API } from 'src/api';
import { useMembers } from 'src/api/services/organisation';
import { useAuth } from 'src/shared/hooks/use-auth';
import { v4 as uuid } from 'uuid';

import { renderWeapons } from './configs';
import { SelectionToolbar, SelectionToolbarProps } from './ui';
import { BlockCombobox } from './ui/BlockCombobox';
import { useAddNewBlockAtEnd } from './utils';

const { useCallback, useEffect } = React;

const EDITOR_ID = 'block-editor';

export interface BlockEditorProps {
  editor?: PlateEditor<any>;
  _decorate?: unknown;
  value?: TDescendant[];
  onChange?: (value: TDescendant[]) => void;
  isReadOnly?: boolean;
  id?: string;
  decorate?: any;
  plugins?: PlatePlugin[];
  editableProps?: EditableProps;
  selectionToolbarProps?: SelectionToolbarProps;
  addNewBlockConfig?: {
    style?: React.CSSProperties;
  };
  wsConnected?: boolean;
  showWebSocketError?: boolean;
  children?: React.ReactNode;
  paddingBottomOnAddNewBlock?: string;
  mode?: 'document' | 'lms' | 'chat';
  onEnter?: (keyboardEvent: React.KeyboardEvent<HTMLDivElement>) => void;

  onTag?({ blockId, text, tag }: { blockId: string; text: string; tag: string }): void;
}

export const BlockEditor = ({
  editor,
  value,
  onChange,
  isReadOnly,
  id = EDITOR_ID,
  plugins,

  editableProps,
  selectionToolbarProps,
  addNewBlockConfig,
  children,
  onTag,
  paddingBottomOnAddNewBlock = '40vh',
  onEnter,
  mode = 'document',
  showWebSocketError = false,
  wsConnected = true,
}: BlockEditorProps): JSX.Element => {
  const editorT = editor || usePlateEditorRef(id);
  const { removeTask, setEditorId, setMembers, setCurrentUser } = useSlatePluginsStore();
  const onAddNewBlock = useAddNewBlockAtEnd(editorT as any);

  const { members } = useMembers();
  const authData = useAuth((s) => s.userDetails);
  const isPublicFile = useAuth((s) => s.isPublicFile);
  const initialValue = [{ type: 'p', children: [{ text: '' }] }];
  useComments((s) => s.activeComment);

  const handleOnChange = useCallback((newValue) => {
    onChange?.(newValue);

    console.log('Synced', newValue);
    if (globalThis._SLATECONTENTSXX) {
      console.log('slate content', globalThis?._SLATECONTENTSXX);
    }
    setTimeout(() => {
      onChange?.(newValue);
    }, 0);
  }, []);

  useEffect(() => {
    setEditorId(id);
    useSlatePluginsStore.setState({ uploadImage: handleUploadImage });
    return () => comboboxActions.reset();
  }, [id]);

  useEffect(() => {
    setMembers(members?.data?.data || []);
  }, [members]);

  useEffect(() => {
    setCurrentUser({
      avatar: authData?.avatarUrl || '',
      email: authData?.email || '',
      firstname: authData?.memberInformation?.firstname || '',
      id: authData?.memberInformation?.memberId || '',
      lastname: authData?.memberInformation?.lastname || '',
    });
  }, [authData]);

  return (
    <div>
      <Plate
        onChange={handleOnChange}
        editor={editorT}
        editableProps={{
          readOnly: isReadOnly,
          onKeyDown: (e) => onEnter?.(e),
          autoFocus: false,
          // TODO: handle drag & drop correctly
          // Refer: https://github.com/ianstormtaylor/slate/blob/main/docs/libraries/slate-react.md#event-handling
          onDrop: () => true,
          onDragStart: () => true,
          onDragEnd: () => true,
          scrollSelectionIntoView: () => null,
          ...editableProps,
        }}
        value={value}
      >
        <div className={`${wsConnected ? 'opactiy-1' : 'opacity-25'}`}>
          {children}
          {!isReadOnly && <SelectionToolbar {...selectionToolbarProps} />}
          <Comments editorId={id} isPublicFile={isPublicFile} />
          <BlockCombobox<{
            value: string;
            name: string;
            icon: any;
            type: string;
            subText: string;
          }>
            onRenderItem={(item) => (
              <div className={cn('text-gray-600 flex items-center p-12')}>
                <div className="flex items-center">
                  <span className="mr-8 text-5xl flex">
                    {item.item?.data?.icon && <item.item.data.icon />}
                  </span>
                  <div className="text-xs flex flex-col">
                    <span className={cn('text-black')}>{item.item.data.name}</span>
                    <span className="text-gray-500">{item.item.data.subText}</span>
                  </div>
                </div>
              </div>
            )}
            items={renderWeapons[mode].map((obj, index) => ({
              key: `${index}`,
              text: obj.name,
              data: obj,
            }))}
          />

          <MentionCombobox
            onRenderItem={(e) => (
              <div
                key={e.item.key}
                className="px-8 py-7 rounded-sm min-w-btn-lg w-full hover:bg-gray-100 cursor-pointer align-middle active:text-primary-500 font-medium text-xs flex items-center justify-between"
              >
                <div className="flex font-regular justify-between items-center">
                  <div className="flex items-center">
                    <Avatar
                      src={e.item.data.avatar}
                      name={e.item.data.name}
                      size="sm"
                      wrapperClassName="border"
                    />
                    <div className="flex flex-col ml-10">
                      <span className="text-sm text-black">{e.item.data.name}</span>
                      <span className="text-xs">{e.item.data.email}</span>
                    </div>
                  </div>
                </div>
                <span className="ml-26 opacity-75 text-sm">{e.item.data.role}</span>
              </div>
            )}
            items={
              members?.data?.data?.map((obj) => ({
                key: obj.memberId,
                text: `${obj.firstname}`,
                data: {
                  name: obj.firstname,
                  value: obj.memberId,
                  avatar: obj.avatar,
                  role: obj.role,
                  email: obj.email,
                },
              })) || []
            }
          />
        </div>
        {!wsConnected && showWebSocketError && <WebSocketErrorMessage />}
      </Plate>
      <div
        style={{ paddingBottom: paddingBottomOnAddNewBlock, ...addNewBlockConfig?.style }}
        onClick={onAddNewBlock}
      ></div>
    </div>
  );
};

const handleUploadImage = async (e) => {
  const data = new FormData();
  data.append('image', e);
  const response = await API.post('/creation/file', data, {
    headers: { ['Content-Type']: 'multipart/form-data' },
  });
  return response.data?.data?.message;
};
const WebSocketErrorMessage = () => {
  return (
    <div
      style={{ left: '22%', top: '10%' }}
      className="opacity-1 bg-white sticky w-575 text-center z-20 shadow-md border py-20 text-sm text-gray-600 rounded-sm"
    >
      You are currently offine. Please refresh to sync the file.
    </div>
  );
};
